import { useEffect } from "react";
import type { GalleryItem } from "@/mocks/gallery";

interface LightboxProps {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (target: number) => void;
}

export default function Lightbox({ items, index, onClose, onPrev, onNext, onSelect }: LightboxProps) {
  const current = items[index];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onPrev();
      if (event.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-foreground-950/95 animate-soft-in">
      <div className="flex items-center justify-between px-4 py-4 md:px-8">
        <div className="text-xs text-background-200">
          {index + 1} / {items.length}
          <span className="ml-3 text-background-400">{current.category}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭大图"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-background-100 transition-colors hover:bg-background-50/20"
        >
          <i className="ri-close-line text-xl" aria-hidden="true"></i>
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center px-4 pb-4 md:px-16">
        <button
          type="button"
          onClick={onPrev}
          aria-label="上一张"
          className="absolute left-2 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-background-100 transition-colors hover:bg-background-50/25 md:left-6 md:h-12 md:w-12"
        >
          <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
        </button>

        <figure className="flex max-h-full w-full max-w-5xl flex-col items-center">
          <div className="flex max-h-[65vh] w-full items-center justify-center overflow-hidden rounded-lg">
            <img
              src={current.image}
              alt={`${current.title} - 张柏芝${current.category}大图`}
              title={`${current.title} - 张柏芝写真图集`}
              className="max-h-[65vh] w-auto max-w-full rounded-lg object-contain"
            />
          </div>
          <figcaption className="mt-5 max-w-2xl text-center">
            <p className="font-heading text-base text-background-50 md:text-lg">{current.title}</p>
            <p className="mt-2 text-xs leading-6 text-background-300 md:text-[13px]">{current.caption}</p>
          </figcaption>
        </figure>

        <button
          type="button"
          onClick={onNext}
          aria-label="下一张"
          className="absolute right-2 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-background-100 transition-colors hover:bg-background-50/25 md:right-6 md:h-12 md:w-12"
        >
          <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
        </button>
      </div>

      <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-6 md:justify-center md:px-8">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={`查看第 ${i + 1} 张：${item.title}`}
            className={`h-14 w-14 shrink-0 cursor-pointer overflow-hidden rounded-md border-2 transition-all md:h-16 md:w-16 ${
              i === index ? "border-primary-400 opacity-100" : "border-transparent opacity-50 hover:opacity-80"
            }`}
          >
            <img
              src={item.image}
              alt={`${item.title} 缩略图`}
              className="h-full w-full object-cover object-top"
            />
          </button>
        ))}
      </div>
    </div>
  );
}